<template>
  <section id="rechargeTransactionSelectList">
    <h3 class="offset-bottom-2">{{ $t('rechargingtransactions.overview_headline') }}</h3>

    <transition name="fade" mode="out-in" appear>
      <VueGoodTable ref="rechargeTransactionOverviewList" v-bind="table">
        <template v-if="hasActionColumn" #table-row-after="{ row, index }">
          <td>
            <VButton class="btn btn-default btn-small" label="rechargingtransactions.details_button_label" @click.stop="selectRow(row, index)" />
          </td>
        </template>
        <template #table-column="{ column }">
          <VTableHeader :columns="table.columns" :column="column" />
        </template>
        <div slot="emptystate" class="center-align text-disabled">
          {{ $t('common.errorNoData') }}
        </div>
      </VueGoodTable>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/Button';
import TableMixin from '@/mixins/table';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import { RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS } from '@route-types';
import { formatDateTime } from '@/utils/date';
import { selectListColumns, autoTopupIdField } from './selectListColumns';
import formatCurrency from '@/mixins/formatCurrency';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';

export default {
  name: 'RechargeTransactionsList',

  components: {
    VueGoodTable,
    VButton,
    VTableHeader,
  },

  mixins: [TableMixin, TableUtilitiesMixin, formatCurrency],

  props: {
    rows: {
      type: Array,
      required: true,
    },
  },

  data() {
    const data = {
      transactionId: null,
      table: {
        columns: selectListColumns,
        rows: null,
        defaultSortBy: { field: 'transactionId', type: 'desc' },
      },
    };

    /* use store getter directly since computed getters aren't mapped yet */
    if (!this.$store.getters['system/hasShowCCTransactionView']) data.table.onClick = null;
    return data;
  },

  computed: {
    ...mapGetters('system', ['hasShowCCTransactionView']),

    hasActionColumn() {
      return this.table.columns.some(col => !!col.action);
    },

    routeParamsTransactionId() {
      return this.$route.params.transactionId;
    },

    routeParamsAutoTopupId() {
      return this.$route.params.autoTopupId;
    },
  },

  watch: {
    rows: {
      handler: 'onRowsChanged',
      immediate: true,
    },
    routeParamsTransactionId: 'onTransactionIdChanged',
    routeParamsAutoTopupId: {
      handler: 'onAutoTopupIdChanged',
      immediate: true,
    },
  },

  created() {
    this.prepareColumns();
  },

  mounted() {
    this.$watch(
      () => this.$refs.rechargeTransactionOverviewList?.columnFilters[autoTopupIdField],
      newVal => {
        this.onAutoTopupFilterValueChanged(newVal);
      }
    );
  },

  methods: {
    onTransactionIdChanged(val) {
      if (!val) {
        this.transactionId = null;
        return;
      }

      const transactionId = parseInt(val, 10);
      const index = this.table.rows.findIndex(row => row.transactionId === transactionId);
      if (index < 0) {
        this.transactionId = null;
      } else {
        this.selectRow({ originalIndex: index }, index);
      }
    },

    onAutoTopupIdChanged(autoTopupId) {
      this.setColumnFilter(autoTopupIdField, String(autoTopupId));
    },

    onRowsChanged() {
      this.table.rows = this.translateRows(this.rows);

      this.addFilterDropDownListByField(this.table, 'rechargingMethod');
      this.addFilterDropDownListByField(this.table, 'transactionStateId');
      this.addFilterDropDownListByField(this.table, autoTopupIdField);
      this.handleDefaultSearchFields(this.table);
      this.onTransactionIdChanged(this.routeParamsTransactionId);
    },

    onAutoTopupFilterValueChanged(newAutoTopupFilterValue) {
      let autoTopupIdRouteValue;
      if (!newAutoTopupFilterValue) {
        autoTopupIdRouteValue = null;
      } else if (newAutoTopupFilterValue === this.emptyPlaceholder) {
        autoTopupIdRouteValue = 'none';
      } else {
        autoTopupIdRouteValue = newAutoTopupFilterValue;
      }

      if (autoTopupIdRouteValue === this.routeParamsAutoTopupId) return;

      this.$router
        .replace({
          params: {
            ...this.$route.params,
            autoTopupId: autoTopupIdRouteValue,
            disableScrolling: true,
          },
        })
        .catch(() => {});
    },

    translateRows(rows) {
      if (rows) {
        rows = rows.map(oldRow => {
          const row = Object.assign({}, oldRow);
          row.topUpTime = row.topUpTime ? formatDateTime(row.topUpTime) : formatDateTime(row.transactionStart);
          row.rechargingMethod = this.$t(`rechargingtransactions.method_${row.rechargingMethod}`);
          row.amount = this.formatCurrency(row.amount, row.currency);
          row.transactionStateId = this.$t(`rechargingtransactions.transaction_state_${String(row.transactionStateId)}`);
          row.serialNumber = !row.serialNumber ? ' ' : String(row.serialNumber);
          row[autoTopupIdField] = !row[autoTopupIdField] ? ' ' : String(row[autoTopupIdField]);
          return row;
        });
      }
      return rows;
    },

    selectRow(row, index) {
      this.setAriaSelected(index);

      if (this.table.rows[row.originalIndex] !== undefined) {
        this.transactionId = this.table.rows[row.originalIndex].transactionId;
        this.$router
          .push({
            name: RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS,
            params: {
              ...this.$route.params,
              transactionId: this.transactionId,
              disableScrolling: null,
            },
          })
          .catch(() => {});
      }
    },

    tableRowStyle(row) {
      if (this.transactionId !== null && row.transactionId === this.transactionId) {
        return 'brand';
      }
      return '';
    },

    prepareColumns() {
      this.table.columns = selectListColumns.filter(col => {
        if (this.hasShowCCTransactionView) {
          return !col.customerOnly;
        }
        return !col.agentOnly;
      });
    },

    setColumnFilter(field, value) {
      if (!this.hasShowCCTransactionView) return;
      const columnIndex = this.table.columns.findIndex(item => item.field === field);

      if (!value) {
        this.table.columns[columnIndex].filterValue = null;
      } else if (value === 'none') {
        this.table.columns[columnIndex].filterValue = this.emptyPlaceholder;
      } else {
        if (this.table.columns[columnIndex].filterOptions.includes(value)) {
          this.table.columns[columnIndex].filterValue = value;
        } else {
          this.table.columns[columnIndex].filterValue = null;
        }
      }
    },
  },
};
</script>
